import React from 'react';
import { Code2, BarChart3, ShoppingCart, Layers, CheckCircle2, ArrowRight } from 'lucide-react';

const services = [
  {
    icon: <Code2 className="text-white" size={40} />,
    title: 'Web Platforms & Applications',
    desc: 'Custom web platforms and applications designed to support advanced logic, integrations, and scalable architectures.',
    features: [
      'Advanced logic and business rules',
      'Third-party integrations',
      'Scalable cloud architecture',
      'Modern frameworks and best practices',
      'Performance optimization',
      'Long-term maintainability'
    ]
  },
  {
    icon: <BarChart3 className="text-white" size={40} />,
    title: 'Dashboards & Internal Systems',
    desc: 'Secure, data-driven dashboards and administrative tools built for operational efficiency and real-world use.',
    features: [
      'Real-time data visualization',
      'Role-based access control',
      'Custom reporting and analytics',
      'Intuitive user interfaces',
      'Data security and compliance',
      'Operational efficiency tools'
    ]
  },
  {
    icon: <ShoppingCart className="text-white" size={40} />,
    title: 'E-Commerce Solutions',
    desc: 'Scalable e-commerce platforms with custom workflows, payment integrations, and performance-focused infrastructure.',
    features: [
      'Custom checkout workflows',
      'Payment gateway integrations',
      'Inventory management',
      'High-traffic optimization',
      'Mobile-responsive design',
      'Scalable infrastructure'
    ]
  },
  {
    icon: <Layers className="text-white" size={40} />,
    title: 'Custom Software Development',
    desc: 'Tailored software solutions engineered to meet your specific business requirements from concept to deployment.',
    features: [
      'Requirements analysis',
      'Custom architecture design',
      'Agile development process',
      'Quality assurance and testing',
      'Deployment and DevOps',
      'Ongoing support and maintenance'
    ]
  },
];

export default function Services() {
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden py-20">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-4 sm:mb-6 tracking-tight leading-tight">
            Services
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed font-medium px-2">
            Detailed breakdown of our offerings. We design and develop robust digital platforms for businesses that require more than standard websites.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16 md:mb-20">
          {services.map((service, i) => (
            <div 
              key={i} 
              className="group relative rounded-2xl p-6 sm:p-8 md:p-10 flex flex-col transition-all duration-300 hover:-translate-y-2"
              style={{
                background: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
                e.currentTarget.style.boxShadow = '0 12px 48px 0 rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(0, 0, 0, 0.1)';
              }}
            >
              <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300 flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <div className="scale-90 sm:scale-100">
                  {service.icon}
                </div>
              </div>
              <h3 className="font-extrabold text-xl sm:text-2xl md:text-3xl text-white mb-3 sm:mb-4">{service.title}</h3>
              <p className="text-white/80 text-base sm:text-lg mb-4 sm:mb-6 leading-relaxed">{service.desc}</p>
              
              <div className="mt-auto">
                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="text-white/60 mt-0.5 flex-shrink-0" size={20} />
                      <span className="text-white/70 text-sm sm:text-base">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* What We Deliver Section */}
        <div className="mb-12 sm:mb-16 md:mb-20">
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-6 sm:mb-8 text-center tracking-tight">
              What We Deliver
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2 sm:mb-3">Architecture-First</div>
                <p className="text-white/70 text-sm sm:text-base">Strong technical foundations designed to support growth and change</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2 sm:mb-3">Engineering Excellence</div>
                <p className="text-white/70 text-sm sm:text-base">Modern frameworks, best practices, and disciplined development processes</p>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-2 sm:mb-3">Scalable Delivery</div>
                <p className="text-white/70 text-sm sm:text-base">Solutions built to evolve as your business grows</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-6 sm:p-8 md:p-12 max-w-4xl mx-auto"
            style={{
              background: 'rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 sm:mb-6 tracking-tight px-2">
              Can we solve your problem?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed px-2">
              If your organization needs a reliable partner for complex web platforms, dashboards, or e-commerce systems, we're ready to help.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg text-white transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
              }}
            >
              Contact Us
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

